import { Play } from '@tunasong/icons'
import type { GroupingQuestion, GroupingVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './grouping-feedback.module.css'

export interface GroupingFeedbackProps {
  question: GroupingQuestion
  verdict: GroupingVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Rhythm Trainer Grouping ID post-answer reveal. Names the correct grouping
 * (and, on incorrect, the learner's pick), shows the level-resolved lexicon
 * body for the grouping, and offers Hear-again + Next. Mirrors MeterFeedback.
 *
 * Spec: docs/superpowers/specs/2026-06-01-rhythm-trainer-v1.5-design.md §5
 */
export const GroupingFeedback: FC<GroupingFeedbackProps> = props => {
  const { question, verdict, onHearAgain, onNext } = props
  const correctEntry = verdict.entry
  const pickedEntry =
    question.options.find(o => o.groupingId === verdict.pickedGroupingId) ?? correctEntry
  const lexicon = useTheoryCopy(correctEntry.lexiconId)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {verdict.correct ? (
          <>That was {correctEntry.label}.</>
        ) : (
          <>
            That was {correctEntry.label}. You picked {pickedEntry.label}.
          </>
        )}
      </p>
      {lexBody ? <p className={styles.body}>{lexBody}</p> : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
